// feedUtils.js
'use strict';
import { LRUCache } from 'lru-cache';

import {
  FeedNameType,
  SUPPORTED_FEED_QUERIES,
  SANITIZE_FEED_QUERIES,
} from '../resource/feedConstants.js';

const cache = new LRUCache({ max: 256 });

/**
 * Sort by alphabet ascending
 * @param {string} a - a item
 * @param {string} b - b item
 */
export const sortByAlphabetAscending = (a, b) => (b > a ? -1 : 1);

/**
 * Get sorted queries
 * @param {array} {filters} - feed filters.
 * @param {array} {sorts} - feed sorts.
 * @param {number} {page} - page number.
 * @param {number} {limit} - page size.
 * @param {number} {timestamp} - timestamp.
 * @param {number|undefined} {noRedirect} - no redirect.
 * @return {array} Return sorted queries
 */
export const getSortedQueries = ({
  filters = [],
  sorts = [],
  page,
  limit,
  timestamp,
  noRedirect,
} = {}) => {
  const querySet = new Set([
    ...filters.map(filter => `filters=${filter}`),
    ...sorts.map(sort => `sorting=${sort}`),
  ]);
  if (page != null) {
    querySet.add(`page=${page}`);
  }
  if (limit != null) {
    querySet.add(`limit=${limit}`);
  }
  if (timestamp != null) {
    querySet.add(`_=${timestamp}`);
  }
  if (noRedirect != null) {
    querySet.add(`no-redirect=${noRedirect}`);
  }
  const queries = [...querySet].sort(sortByAlphabetAscending);
  return queries;
};

/**
 * Get sorted feed names
 * @param {string} {feed} - feed name or feed id.
 * @return {array} Return sorted feed name
 */
export const getSortedFeedNames = ({ feed = '' } = {}) => {
  const feedNameSet = new Set();
  const url = new URL(feed, 'https://swag.live');
  const alias = url.pathname.replace(/^\//, '');
  const search = url.search;
  alias
    .split('&')
    .filter(s => s)
    .forEach(feedName => feedNameSet.add(feedName));
  const names = [...feedNameSet].sort(sortByAlphabetAscending);
  // feed might include query string, add query string on last item
  if (search && names.length) {
    const lastIndex = names.length - 1;
    names[lastIndex] = `${names[lastIndex]}${search}`;
  }
  return names;
};

/**
 * Parse query options.
 * @param {array} {options} - selected query options.
 * @return {object} Return parsed option object.
 */
export const parseQueryOptions = ({ options = [] } = {}) => {
  const feedNameSet = new Set();
  let querySet = new Set();

  options
    .filter(option => option)
    .forEach(option => {
      const url = new URL(option, 'https://swag.live');
      const alias = url.pathname.replace(/^\//, '');
      const search = url.search;
      if (alias) {
        alias
          .split('&')
          .filter(feedName => feedName)
          .forEach(feedName => feedNameSet.add(feedName));
      }
      if (search) {
        [...url.searchParams.entries()].forEach(([key, value]) => {
          // Special handling for sorting parameter
          if (key === 'sorting') {
            // only keep the latest one
            querySet = new Set(
              [...querySet].filter(q => !q.startsWith('sorting='))
            );
          }
          querySet.add(`${key}=${value}`);
        });
      }
    });

  const intersectedFeedNames = [...feedNameSet].sort(sortByAlphabetAscending);
  const joinedFeedName = intersectedFeedNames.join('&');
  const queries = [...querySet].sort(sortByAlphabetAscending);
  const result = {
    intersectedFeedName: joinedFeedName,
    intersectedFeedNames,
    filters: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return (
          u.searchParams.get('filters') || u.searchParams.get('filters_v2')
        );
      })
      .filter(item => item),
    sorts: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return u.searchParams.get('sorting');
      })
      .filter(item => item),
    queries,
    feedNameWithQuery: `${joinedFeedName}${
      queries.length ? `?${queries.join('&')}` : ''
    }`,
  };
  return result;
};

/**
 * Get feed type from feed.
 * @param {string} {feedName} - feed name.
 * @return {FeedNameType} Return feed type.
 */
export const getFeedNameType = ({ feedName = '' } = {}) =>
  /^(user|discover|leaderboard)(_|-)/i.test(feedName)
    ? FeedNameType.USERS
    : /^(hashtag)(_|-)/i.test(feedName)
      ? FeedNameType.HASHTAGS
      : /^(category)(_|-)/i.test(feedName)
        ? FeedNameType.CATEGORIES
        : FeedNameType.MESSAGES;

/**
 * Get category from category and location.search sorting and filters
 * @param {string} {category} - feed category.
 * @param {string} {locationSearch} - location search.
 * @return {string} The feed category.
 */
export const getFeedCategory = ({ category = '', search = '' } = {}) => {
  const categoryUrl = new URL(category, 'https://swag.live');
  const categoryPath = categoryUrl.pathname.replace(/^\//, '');
  const incomingSearchParams = new URLSearchParams(search);
  const mergedSearchParams = new URLSearchParams();

  [
    ...incomingSearchParams.entries(),
    ...categoryUrl.searchParams.entries(),
  ].forEach(([key, value]) => {
    // Params who is not allowed
    if (!SUPPORTED_FEED_QUERIES.includes(key)) {
      return;
    }
    // Params who is duplicated
    if (mergedSearchParams.get(key)) {
      return;
    }

    mergedSearchParams.set(key, value);
  });

  const searchString = mergedSearchParams.toString();
  const cacheKey = `${categoryPath}:${searchString}`;
  let result = cache.get(cacheKey);
  if (!result) {
    result = `${categoryPath}${searchString ? `?${searchString}` : ''}`;
    cache.set(cacheKey, result);
  }
  return result;
};

/**
 * 1. Check if the target category has all the feed alias from base category.
 * 2. The order of the arguments (probeCategory, targetCategory) matters:
 *    All the alias in first argument `probeCategory` would be confirmed to exist in the second argument `targetCategory`,
 *    but we don't promise the opposite.
 * @param {string} {probeCategory} - all the feed alias in this must be found in `targetCategory`
 * @param {string} {targetCategory} - the category we have to check if it contains all the `probeCategory`s alias
 * @return {bool} If all the feed alias in `probeCategory` can be found in `targetCategory`.
 */
export const isAllAliasesInTargetCategory = ({
  probeCategory,
  targetCategory,
}) => {
  const baseUrl = 'https://swag.live';
  const url1 = new URL(probeCategory, baseUrl);
  const url2 = new URL(targetCategory, baseUrl);

  const aliases1 = url1.pathname.slice(1).split('&');
  const aliases2 = url2.pathname.slice(1).split('&');

  return aliases1.every(alias => aliases2.includes(alias));
};

/**
 * Parse options V2.
 * @param {string} {baseFeedName} - base feed name.
 * @param {array} {options} - options.
 * @return {object} Return parsed options object.
 */
export const parseOptionsV2 = ({ baseFeedName = '', options = [] }) => {
  const feedNameSet = new Set();
  const querySet = new Set();
  let defaultSorting = '';

  // Initialize feedNameSet and querySet based on the baseFeedName.
  // For example, baseFeedName: 'user_livestream-v2?sorting=desc:trending_ranking&ui=livestream-square',
  // the initial feedNameSet will contain 'user_livestream-v2',
  // and the initial querySet will contain 'sorting=desc:trending_ranking' and 'ui=livestream-square'.
  const url = new URL(baseFeedName, 'https://swag.live');
  const alias = url.pathname.replace(/^\//, '');

  if (alias) {
    alias.split('&').forEach(feedName => feedNameSet.add(feedName));
  }

  url.searchParams.forEach((searchValue, searchKey) => {
    if (searchKey && searchValue && SANITIZE_FEED_QUERIES.includes(searchKey)) {
      querySet.add(`${searchKey}=${searchValue}`);

      if (searchKey === 'sorting') {
        defaultSorting = searchValue;
      }
    }
  });

  // Converts options into a mapping object
  // e.g: [
  //   'LIVESTREAM_FEED_FILTERS_CATEGORY/user_livestream-v2?filters_v2=category:toy',
  //   'LIVESTREAM_FEED_FILTERS_CATEGORY/user_livestream-v2?filters_v2=category:live_sex',
  //   'LIVESTREAM_FEED_FILTERS_EXPLICITNESS_SINGLE/user_livestream-v2?filters_v2=category:green',
  //   'LIVESTREAM_FEED_FILTERS_REGION/user_livestream-v2&user_creators?filters_v2=country:tw',
  // ]
  // => {
  //   LIVESTREAM_FEED_FILTERS_CATEGORY: [
  //     'user_livestream-v2?filters_v2=category:toy',
  //     'user_livestream-v2?filters_v2=category:live_sex',
  //   ],
  //   LIVESTREAM_FEED_FILTERS_EXPLICITNESS_SINGLE: [
  //     'user_livestream-v2?filters_v2=category:green',
  //   ],
  //   LIVESTREAM_FEED_FILTERS_REGION: [
  //     'user_livestream-v2&user_creators?filters_v2=country:tw',
  //   ],
  // }
  const optionsMap = options.reduce((acc, groupedOption) => {
    const [filterGroup, option] = groupedOption.split('/');

    // add the new filter value to existing array and remove duplicates using Set
    const newValue = Array.from(new Set([...(acc[filterGroup] || []), option]));

    return {
      ...acc,
      [filterGroup]: newValue,
    };
  }, {});

  // e.g: {
  //   LIVESTREAM_FEED_FILTERS_CATEGORY: [
  //     'user_livestream-v2?filters_v2=category:toy',
  //     'user_livestream-v2?filters_v2=category:live_sex',
  //   ],
  //   LIVESTREAM_FEED_FILTERS_EXPLICITNESS_SINGLE: [
  //     'user_livestream-v2?filters_v2=category:green',
  //   ],
  //   LIVESTREAM_FEED_FILTERS_REGION: [
  //     'user_livestream-v2&user_creators?filters_v2=country:tw',
  //   ],
  // }
  // => {
  //   feedNameSet: ['user_livestream-v2', 'user_creators'],
  //   querySet: ['filters_v2=category:toy|category:live_sex', 'filters_v2=category:green', 'filters_v2=country:tw'],
  // }
  Object.values(optionsMap).forEach((options = []) => {
    let currentGroupQuerySet = new Set();
    let searchGroupKey = '';

    options.sort().forEach(option => {
      const url = new URL(option, 'https://swag.live');
      const alias = url.pathname.replace(/^\//, '');

      if (alias) {
        alias.split('&').forEach(feedName => feedNameSet.add(feedName));
      }

      url.searchParams.forEach((searchValue, searchKey) => {
        if (searchKey && searchValue) {
          searchGroupKey = searchKey;
          currentGroupQuerySet.add(searchValue);
        }
      });
    });

    // Remove existing sorting query if new one is being added
    // to ensure only one sorting parameter exists at a time
    if (searchGroupKey === 'sorting') {
      [...querySet].forEach(query => {
        if (query.startsWith('sorting=')) {
          querySet.delete(query);
        }
      });
    }

    // Combine multiple filter values from the same group using '|' as a separator
    // e.g: 'filters_v2=category:toy|category:live_sex'
    querySet.add(`${searchGroupKey}=${[...currentGroupQuerySet].join('|')}`);
  });

  const intersectedFeedNames = [...feedNameSet].sort(sortByAlphabetAscending);
  const joinedFeedName = intersectedFeedNames.join('&');
  const queries = [...querySet].sort(sortByAlphabetAscending);

  return {
    intersectedFeedName: joinedFeedName,
    intersectedFeedNames,
    filters: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return u.searchParams.get('filters_v2');
      })
      .filter(item => item),
    sorts: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return u.searchParams.get('sorting');
      })
      .filter(item => item)
      .filter(item => item !== defaultSorting),
    queries,
    feedNameWithQuery: `${joinedFeedName}${
      queries.length ? `?${queries.join('&')}` : ''
    }`,
  };
};

/**
 * Parse option with filter group map.
 * @param {array} {options} - selected query options.
 * @param {object} {filterGroupMap} - filter group map.
 * @return {array} Return parsed option with filter group map.
 */
export const parseOptionWithFilterGroupMap = ({
  options = [],
  filterGroupMap = {},
}) => {
  return options.map(option => {
    // Find matching filterGroup using Object.entries()
    const matchingGroup = Object.entries(filterGroupMap).find(([, options]) =>
      options.includes(option)
    );

    // Return "group/option" format if match found, otherwise return original option
    return matchingGroup ? `${matchingGroup[0]}/${option}` : option;
  });
};

/**
 * Check if the option is default sorting.
 * @param {string} {baseFeedName} - base feed name.
 * @param {string} {option} - option.
 * @return {boolean} Return true if the option is default sorting.
 */
export const isDefaultSorting = ({ baseFeedName = '', option = '' }) => {
  const urlSorting = new URL(
    baseFeedName,
    'https://swag.live'
  ).searchParams.get('sorting');
  return urlSorting && option.includes(urlSorting);
};

/**
 * Filter feedName to only include searchParams that are supported by the API
 * @param {string} feedName - The feedName string that may contain query parameters
 * @return {string} The filtered feedName with only supported query parameters
 */
export const sanitizeFeedQuery = ({ feedName = '' }) => {
  if (!feedName) return '';

  const url = new URL(feedName, 'https://swag.live');
  const keys = [...url.searchParams.keys()];
  if (keys.length === 0) return feedName;

  keys
    .filter(key => !SANITIZE_FEED_QUERIES.includes(key))
    .forEach(key => url.searchParams.delete(key));

  const alias = url.pathname.replace(/^\//, '');
  const query = url.search;

  return `${alias}${decodeURIComponent(query)}`;
};
